import { useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LeadHandoverMode,
  type LeadHandover,
  type LeadHandoverPreview,
  type ManagedUser,
} from '@/api/users'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useColleagues } from '@/hooks/useColleagues'
import { userLabel } from '@/lib/userDisplay'

export type LockOrDelete = 'lock' | 'delete'

/**
 * What is being confirmed, with the leads already counted — the dialog opens at its final size
 * rather than growing once the numbers arrive. `preview` is null when they could not be counted.
 */
export type Confirmation = {
  action: LockOrDelete
  preview: LeadHandoverPreview | null
}

/**
 * Asks before locking or deleting an account — and, when leads are assigned to it, who takes them
 * over. Somebody who cannot log in cannot work a lead, so the moment an account is closed is the
 * moment to decide, not months later when a customer asks why nobody called back.
 *
 * The choice only appears when there is something to choose about. A failed count does not block
 * the lock: locking somebody out can be urgent, and without an answer the backend does the
 * sensible default — every lead back to whoever wrote it, the rest to nobody.
 */
export function LockOrDeleteDialog({
  user,
  name,
  confirmation,
  busy,
  onCancel,
  onConfirm,
}: {
  user: ManagedUser
  name: string
  /** Null while the dialog is closed. */
  confirmation: Confirmation | null
  busy: boolean
  onCancel: () => void
  onConfirm: (handover: LeadHandover | undefined) => void
}) {
  const { t } = useTranslation()
  const open = confirmation !== null
  const colleagues = useColleagues(open).filter(
    (colleague) => colleague.assignable && colleague.id !== user.id,
  )
  const [mode, setMode] = useState<LeadHandoverMode>(LeadHandoverMode.OWNERS)
  const [fallback, setFallback] = useState('')
  const [assignee, setAssignee] = useState('')

  // What is on screen is the last confirmation asked for, not the current prop: while the dialog
  // animates out the prop is already null, and rendering from it would flip a "delete" title to
  // "lock" for the length of the animation. A new confirmation starts the choice over — adjusted
  // during render, like `AssignLeadDialog`, so the previous answer is never painted first.
  const [shown, setShown] = useState<Confirmation | null>(confirmation)
  if (confirmation !== null && confirmation !== shown) {
    setShown(confirmation)
    setMode(LeadHandoverMode.OWNERS)
    setFallback('')
    setAssignee('')
  }
  const action = shown?.action ?? null

  const counted = shown?.preview ?? null
  const assigned = counted?.assigned ?? 0
  const returnable = counted?.returnable ?? 0
  const unreturnable = assigned - returnable
  const choosing = assigned > 0

  const handover = handoverFor(choosing, mode, unreturnable > 0 ? fallback : undefined, assignee)
  // An empty id is a colleague still to be picked — the one thing that keeps this from going out.
  const complete = handover?.assigneeId !== ''

  return (
    <AlertDialog open={open} onOpenChange={(next) => !next && onCancel()}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            {action === 'delete' ? t('users.delete.title', { name }) : t('users.lock.title', { name })}
          </AlertDialogTitle>
          <AlertDialogDescription>
            {action === 'delete' ? t('users.delete.description') : t('users.lock.description')}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="grid gap-3 text-sm" data-testid="lead-handover">
          {shown && !counted && (
            <p className="text-muted-foreground">{t('users.handover.countFailed')}</p>
          )}
          {counted && !choosing && (
            <p className="text-muted-foreground">{t('users.handover.none', { name })}</p>
          )}
          {choosing && (
            <fieldset className="grid gap-3" disabled={busy}>
              <legend className="mb-3 font-medium">
                {t('users.handover.intro', { name, count: assigned })}
              </legend>

              <HandoverOption
                value={LeadHandoverMode.OWNERS}
                checked={mode === LeadHandoverMode.OWNERS}
                onChange={setMode}
                label={t('users.handover.owners')}
                hint={returnable > 0 ? t('users.handover.ownersHint', { count: returnable }) : undefined}
              >
                {unreturnable > 0 && (
                  <ColleagueSelect
                    id="handover-fallback"
                    label={t('users.handover.fallback', { name, count: unreturnable })}
                    placeholder={t('users.handover.choose')}
                    value={fallback}
                    onChange={setFallback}
                    colleagues={colleagues}
                    disabled={mode !== LeadHandoverMode.OWNERS}
                  />
                )}
              </HandoverOption>

              <HandoverOption
                value={LeadHandoverMode.USER}
                checked={mode === LeadHandoverMode.USER}
                onChange={setMode}
                label={t('users.handover.user')}
              >
                <ColleagueSelect
                  id="handover-assignee"
                  label={t('users.handover.userLabel')}
                  placeholder={t('users.handover.choose')}
                  value={assignee}
                  onChange={setAssignee}
                  colleagues={colleagues}
                  disabled={mode !== LeadHandoverMode.USER}
                />
              </HandoverOption>

              <HandoverOption
                value={LeadHandoverMode.NOBODY}
                checked={mode === LeadHandoverMode.NOBODY}
                onChange={setMode}
                label={t('users.handover.nobody')}
                hint={t('users.handover.nobodyHint')}
              />
            </fieldset>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>{t('common.cancel')}</AlertDialogCancel>
          <AlertDialogAction
            variant="destructive"
            disabled={busy || !complete}
            // Not `onSelect`: the dialog closes itself on action, and the request has to survive
            // that — the caller closes it once the request has settled.
            onClick={(event) => {
              event.preventDefault()
              onConfirm(handover)
            }}
          >
            {action === 'delete' ? t('users.delete.confirm') : t('users.lock.confirm')}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/**
 * What goes to the backend. Nothing at all when no leads are assigned: there is nothing to decide,
 * and the backend's default does the right thing for an empty list.
 *
 * @param fallback who gets the leads that cannot go back to their writers, when there are any.
 */
function handoverFor(
  choosing: boolean,
  mode: LeadHandoverMode,
  fallback: string | undefined,
  assignee: string,
): LeadHandover | undefined {
  if (!choosing) {
    return undefined
  }
  switch (mode) {
    case LeadHandoverMode.USER:
      return { mode, assigneeId: assignee }
    case LeadHandoverMode.OWNERS:
      return fallback === undefined ? { mode } : { mode, assigneeId: fallback }
    case LeadHandoverMode.NOBODY:
      return { mode }
  }
}

/**
 * One way the leads can go. A native radio rather than a component: three options that are all
 * visible at once are what a radio group is.
 *
 * The control underneath is always there, and only enabled for the option that is chosen. Shown
 * and hidden with the choice, it made the dialog grow and shrink under the pointer with every
 * click, and the next radio was never where it had just been.
 */
function HandoverOption({
  value,
  checked,
  onChange,
  label,
  hint,
  children,
}: {
  value: LeadHandoverMode
  checked: boolean
  onChange: (mode: LeadHandoverMode) => void
  label: string
  hint?: string
  children?: ReactNode
}) {
  const id = `handover-${value.toLowerCase()}`
  return (
    <div className="grid gap-2">
      <div className="flex items-start gap-2">
        <input
          type="radio"
          id={id}
          name="lead-handover"
          value={value}
          checked={checked}
          onChange={() => onChange(value)}
          className="accent-primary mt-0.5 size-4 shrink-0"
        />
        <div className="grid gap-0.5">
          <Label htmlFor={id} className="font-normal">
            {label}
          </Label>
          {hint && <p className="text-muted-foreground text-xs">{hint}</p>}
        </div>
      </div>
      {children && <div className="ml-6">{children}</div>}
    </div>
  )
}

function ColleagueSelect({
  id,
  label,
  placeholder,
  value,
  onChange,
  colleagues,
  disabled,
}: {
  id: string
  label: string
  placeholder: string
  value: string
  onChange: (id: string) => void
  colleagues: ReturnType<typeof useColleagues>
  /** While its option is not the chosen one; the pick is kept for when it is again. */
  disabled: boolean
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id} className="text-muted-foreground text-xs font-normal">
        {label}
      </Label>
      <Select value={value} onValueChange={(next) => next && onChange(next)} disabled={disabled}>
        <SelectTrigger id={id} className="w-full">
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent>
          {colleagues.map((colleague) => (
            <SelectItem key={colleague.id} value={colleague.id}>
              {userLabel(colleague, colleague.id)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  )
}
